"use client";
import React, { useEffect, useRef, useState, useLayoutEffect } from "react";
import { Images, Icons } from "@/app/assets/shared";
import Image from "next/image";
import type { StaticImageData } from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { whenIdle } from "@/lib/whenIdle";

type IndustryItem = {
  title: string;
  description: string;
  icon?: string | StaticImageData;
  image?: string | StaticImageData;
  link?: string;
};

type IndustryProps = {
  industries: IndustryItem[];
  titleStart?: React.ReactNode;
  titleEnd?: React.ReactNode;
  animation?: boolean;
  bg_image?: string | StaticImageData;
  showBgGraphic?: boolean;
  sectionBg?: string;
  cardBg?: string;
  showIcons?: boolean;
  arrowIcon?: string | StaticImageData;
};

const TruncatedText = ({
  description,
  isStatic,
  isExpanded,
  onToggle,
}: {
  description: string;
  isStatic: boolean;
  isExpanded: boolean;
  // eslint-disable-next-line no-unused-vars
  onToggle: (e: React.MouseEvent) => void;
}) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const checkTruncation = () => {
      if (textRef.current) {
        const hasOverflow =
          textRef.current.scrollHeight > textRef.current.clientHeight;
        setIsTruncated(hasOverflow);
      }
    };

    const debouncedCheck = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(checkTruncation, 150); // Only run 150ms after resizing stops
    };

    checkTruncation();
    window.addEventListener("resize", debouncedCheck);
    return () => {
      window.removeEventListener("resize", debouncedCheck);
      clearTimeout(timeoutId);
    };
  }, [description]);

  return (
    <div className="flex flex-col items-start w-full overflow-hidden mb-8">
      <div
        className={`w-full transition-all duration-300 ease-in-out ${
          isExpanded
            ? "max-h-[220px] min-[2000px]:max-h-[260px] overflow-y-auto custom-scrollbar pr-2"
            : "max-h-[none]"
        }`}
      >
        <p
          ref={textRef}
          className={`select-none self-stretch justify-start text-[#636363] text-[length:var(--industry-para)] font-medium leading-[160%] ${
            isExpanded ? "line-clamp-none" : "line-clamp-4"
          }`}
          dangerouslySetInnerHTML={{ __html: description }}
        />
      </div>

      {isTruncated && (
        <div className="mt-2">
          {isStatic ? (
            <button
              onClick={onToggle}
              aria-label={isExpanded ? "Read less" : "Read more"}
              className="inline-flex items-center justify-center p-1 rounded-md text-[#225bd4] hover:bg-[#225bd4] hover:text-white transition-all cursor-pointer z-50 relative"
            >
              {isExpanded ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m18 15-6-6-6 6" />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
              )}
            </button>
          ) : (
            <div className="text-[#225bd4]">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const loadGSAP = async () => {
  const gsapModule = await import("gsap");
  const scrollModule = await import("gsap/ScrollTrigger");

  const gsap = gsapModule.default;
  const ScrollTrigger = scrollModule.default;

  gsap.registerPlugin(ScrollTrigger);

  return { gsap, ScrollTrigger };
};

export default function Industry({
  industries,
  titleStart,
  titleEnd,
  animation = true,
  bg_image,
  showBgGraphic = true,
  sectionBg = "bg-[#D9E2ED]",
  cardBg = "bg-white",
  arrowIcon = Icons.industryArrow,
}: IndustryProps) {
  const sliderRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const gsapRef = useRef<any>(null);
  const scrollTriggerRef = useRef<any>(null);
  const [gsapLoaded, setGsapLoaded] = useState(false);
  const pathname = usePathname();
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  // const isTechnologyPage = ["/"].some((path) => pathname?.endsWith(path));
  const isTechnologyPage = pathname?.trim() === "/";

  const infiniteIndustries = [...industries, ...industries, ...industries];

  const [currentIndex, setCurrentIndex] = useState(industries.length);
  const [itemWidth, setItemWidth] = useState(0);
  const [isLargeScreen, setIsLargeScreen] = useState(false);

  const getImgSrc = (img: string | StaticImageData | undefined): string => {
    if (!img) return "";
    return typeof img === "string" ? img : img.src;
  };

  // bg_image={""} is an explicit "no background graphic" — only fall back to
  // the default when the prop was never passed at all. `getImgSrc(bg_image) ||
  // fallback` collapsed those two cases, so pages that opted out still got
  // industryBannerBg painted behind their cards.
  const bgSrc =
    bg_image !== undefined
      ? getImgSrc(bg_image)
      : getImgSrc(Images?.industryBannerBg);

  const shouldAnimate = animation && isLargeScreen;

  useEffect(() => {
    const handleResize = () => setIsLargeScreen(window.innerWidth >= 1366);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useLayoutEffect(() => {
    let ctx: any;
    let cancelled = false;
    // Defer GSAP load + slider measurement (offsetWidth = forced reflow)
    // until the browser is idle: this section is below the fold, so doing
    // this during hydration only inflates mobile TBT.
    const cancelIdle = whenIdle(() => {
      if (cancelled) return;
      loadGSAP().then(({ gsap, ScrollTrigger }) => {
        if (cancelled) return;
        gsapRef.current = gsap;
        scrollTriggerRef.current = ScrollTrigger;
        setGsapLoaded(true);

        ctx = gsap.context(() => {});
        let lastWidth = 0;

        const initSlider = async () => {
          if (document.fonts) {
            await document.fonts.ready;
          }

          const updateLayout = () => {
            if (!sliderRef.current) return;

            const firstItem = sliderRef.current.querySelector(
              ".industry-item",
            ) as HTMLElement;
            if (firstItem) {
              const newWidth = firstItem.offsetWidth + 30;

              if (Math.abs(newWidth - lastWidth) < 1) return;

              lastWidth = newWidth;
              setItemWidth(newWidth);

              requestAnimationFrame(() => {
                gsap.set(sliderRef.current, {
                  x: -industries.length * newWidth,
                  autoAlpha: 1,
                  force3D: true,
                });
              });
            }
          };

          const observer = new ResizeObserver(() => {
            requestAnimationFrame(updateLayout);
          });

          if (containerRef.current) observer.observe(containerRef.current);

          updateLayout();

          return () => observer.disconnect();
        };

        initSlider();
      });
    });
    return () => {
      cancelled = true;
      cancelIdle();
      ctx?.revert?.();
    };
  }, [industries.length]);

  const goToIndex = (newIndex: number, smooth = true) => {
    const gsap = gsapRef.current;
    if (!gsap || !sliderRef.current || itemWidth === 0) return;
    const targetX = -newIndex * itemWidth;

    gsap.to(sliderRef.current, {
      x: targetX,
      duration: smooth ? 0.5 : 0,
      ease: "power2.out",
      force3D: true,
      lazy: true,
      onComplete: () => {
        if (newIndex <= 0) {
          const resetIndex = industries.length;
          gsap.set(sliderRef.current, { x: -resetIndex * itemWidth });
          setCurrentIndex(resetIndex);
        } else if (newIndex >= industries.length * 2) {
          const resetIndex = industries.length + (newIndex % industries.length);
          gsap.set(sliderRef.current, { x: -resetIndex * itemWidth });
          setCurrentIndex(resetIndex);
        }
      },
    });
    setCurrentIndex(newIndex);
  };

  const handleNext = () => goToIndex(currentIndex + 1);
  const handlePrev = () => goToIndex(currentIndex - 1);

  useEffect(() => {
    if (!shouldAnimate || !containerRef.current) return;
    const allItems = containerRef.current.querySelectorAll(".industry-item");
    allItems.forEach((item, idx) => {
      if (!(item instanceof HTMLElement)) return;
      item.classList.remove("item-1", "item-2", "item-3", "item-4");
      const relativeIdx = idx - currentIndex;
      if (relativeIdx >= 0 && relativeIdx < 4) {
        item.classList.add(`item-${relativeIdx + 1}`);
      }
    });
  }, [currentIndex, shouldAnimate]);

  useEffect(() => {
    const gsap = gsapRef.current;
    const ScrollTrigger = scrollTriggerRef.current;
    if (!shouldAnimate || !gsap || !ScrollTrigger) return;
    const sectionEl = sectionRef.current;
    if (!sectionEl) return;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionEl,
          start: "top 80px",
          end: "+=" + window.innerHeight * 1.3,
          scrub: 1,
          invalidateOnRefresh: true,
        },
      });

      const is2xlViewport = window.innerWidth >= 1536;
      const industryTargetHeight = is2xlViewport
        ? `${Math.round(window.innerHeight * 0.7)}px`
        : `${window.innerWidth > 1500 ? 600 : 500}px`;

      tl.to(sectionEl.querySelectorAll(".industry-item"), {
        height: industryTargetHeight,
        duration: 1,
      });

      if (window.innerWidth >= 1365) {
        tl.to(sectionEl.querySelectorAll(".animation-item"), {
          left: 0,
          transform: "translateX(0)",
          duration: 1,
        });
      }

      tl.fromTo(
        sectionEl.querySelectorAll(".industry-item-content"),
        { opacity: 0 },
        { opacity: 1, duration: 1 },
        "<",
      );
      tl.to(
        sectionEl.querySelectorAll(".industry-bg"),
        { opacity: 1, duration: 0.2 },
        "<",
      );
    }, sectionEl);

    return () => ctx.revert();
  }, [shouldAnimate, industries.length, gsapLoaded]);

  useEffect(() => {
    const ScrollTrigger = scrollTriggerRef.current;
    if (!shouldAnimate || !ScrollTrigger) return;

    const handle = setTimeout(() => {
      ScrollTrigger.refresh();
    }, 400);
    return () => clearTimeout(handle);
  }, [currentIndex, shouldAnimate, gsapLoaded]);

  const sectionClassName = shouldAnimate
    ? `${sectionBg} w-full sticky top-0 industry-section h-[100vh] flex flex-col overflow-hidden`
    : `${sectionBg} w-full relative industry-section-static xl:min-h-[100vh] flex flex-col overflow-hidden`;

  const itemContentClassName = shouldAnimate
    ? `w-[90%] [@media(min-width:1365px)]:w-full ${cardBg} relative lg:static mx-auto flex flex-col items-start lg:justify-around p-[10px] lg:gap-5 [@media(min-width:1365px)]:p-[30px] [@media(min-width:1600px)]:p-[40px] industry-item-content h-[400px] lg:h-[var(--industry-2xl-height)] 2xl:h-[var(--industry-2xl-height)] [@media(min-width:1365px)]:h-[90%] [&_*]:transition-all [&_*]:duration-300 [&_*]:ease-in-out`
    : `w-[90%] [@media(min-width:1365px)]:w-full ${cardBg} relative lg:static mx-auto flex flex-col items-start lg:justify-around gap-6 lg:gap-10 p-[10px] [@media(min-width:1365px)]:p-[30px] [@media(min-width:1600px)]:p-[40px] [@media(min-width:1600px)]:pb-[60px] industry-item-content min-h-[450px] max-h-[600px] h-auto lg:h-[var(--industry-2xl-height)] 2xl:h-[var(--industry-2xl-height)] [@media(min-width:1365px)]:h-[90%] [&_*]:transition-all [&_*]:duration-300 [&_*]:ease-in-out`;

  return (
    <div className={`${shouldAnimate ? "h-[calc(250vh+300px)]" : ""} relative`}>
      <style jsx global>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #225bd4;
          border-radius: 10px;
        }
      `}</style>

      <section className={sectionClassName} ref={sectionRef}>
        {/* The graphic only works as part of the scroll animation: GSAP fades
            it in from opacity-0. Without the animation it just sits at full
            opacity behind the cards, which is why 50 usages had already
            switched it off by hand. Tie it to shouldAnimate so it can't come
            back on a page that forgets the prop. */}
        {showBgGraphic && shouldAnimate && bgSrc && (
          <div
            className="industry-bg pointer-events-none absolute left-1/2 -translate-x-1/2 transition-opacity duration-300 hidden lg:block opacity-0 top-[4%] min-[2000px]:top-[26%] w-[700px] h-[700px]"
            style={{
              backgroundImage: `url(${bgSrc})`,
              backgroundSize: "contain",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
            }}
          />
        )}

        <div
          className={
            (shouldAnimate
              ? "max-w-[5000px] mx-auto w-[calc(100%_-_10%)] h-[100vh] flex flex-col items-center justify-center"
              : "max-w-[5000px] mx-auto w-[calc(100%_-_10%)] h-auto pt-[20px] pb-[30px] sm:pb-[10px] py-[120px] lg:pb-[80px] flex flex-col items-center justify-center") +
            " relative z-10"
          }
        >
          <div className="industry-container flex flex-col relative z-4 w-full">
            <div className="middle flex flex-row md:flex-row justify-between items-end mb-2 pt-2 lg:pt-20">
              {titleStart}
              <div className="technology-tab-switch flex gap-2 sm:gap-[10px] w-max mb-1 sm:mb-2 self-end shrink-0">
                <button
                  onClick={handlePrev}
                  className="cursor-pointer hover:opacity-70 transition-opacity"
                  aria-label="Previous Slide"
                >
                  <Image
                    src={Icons?.slideLeft as any}
                    alt=""
                    width={40}
                    height={40}
                    className="w-[32px] h-[32px] sm:w-[40px] sm:h-[40px]"
                    sizes="(max-width: 1024px) 100vw, 50vw"
                    loading="lazy"
                  />
                </button>

                <button
                  onClick={handleNext}
                  className="cursor-pointer hover:opacity-70 transition-opacity"
                  aria-label="Next Slide"
                >
                  <Image
                    src={Icons?.slideRight as any}
                    alt=""
                    width={40}
                    height={40}
                    className="w-[32px] h-[32px] sm:w-[40px] sm:h-[40px]"
                    sizes="(max-width: 1024px) 100vw, 50vw"
                    loading="lazy"
                  />
                </button>
              </div>
            </div>

            <div
              className="slider-wrapper w-full overflow-hidden"
              ref={containerRef}
            >
              <div
                className="slider-content flex gap-[30px]"
                ref={sliderRef}
                style={{
                  willChange: "transform",
                  visibility: "visible", // Hide until math is done
                  opacity: 1,
                }}
              >
                {infiniteIndustries?.map((item, index) => {
                  const isExpanded = expandedIndex === index;

                  const handleToggle = (e: React.MouseEvent) => {
                    if (!shouldAnimate) {
                      e.preventDefault();
                      e.stopPropagation();
                      setExpandedIndex(isExpanded ? null : index);
                    }
                  };

                  const CardInner = (
                    <>
                      <div
                        className={`transition-all duration-300 ease-in-out mb-10 ${shouldAnimate ? "group-hover:hidden group-hover:h-0 group-hover:mb-0" : ""}`}
                      >
                        {/* src="" resolves to the current page URL, so an item
                            without an icon made the browser re-download the
                            whole HTML document as an image. item.image below is
                            already guarded the same way. */}
                        {item.icon && (
                          <Image
                            src={item.icon}
                            alt="icon"
                            width={50}
                            height={50}
                            className="industry-icon w-[clamp(5rem,2.1743rem+2.3548vw,5.9375rem)] h-[clamp(5rem,2.1743rem+2.3548vw,5.9375rem)] object-cover"
                            sizes="(max-width: 1024px) 100vw, 50vw"
                            loading="lazy"
                          />
                        )}
                      </div>
                      <div
                        className={`flex flex-col gap-2 transition-transform duration-500 ease-in-out w-full ${shouldAnimate ? "group-hover:-translate-y-4" : "group-hover:-translate-y-2"}`}
                      >
                        <h3 className="select-none text-[#00040b] text-[length:var(--industry-title)] font-medium clash-font capitalize leading-[120%]">
                          {item.title}
                        </h3>
                        <TruncatedText
                          description={item.description}
                          isStatic={!shouldAnimate}
                          isExpanded={isExpanded}
                          onToggle={handleToggle}
                        />
                      </div>
                      {item.image && (
                        <div className="w-full h-0 opacity-0 translate-y-10 transition-all duration-500 ease-in-out group-hover:h-full group-hover:opacity-100 group-hover:translate-y-16">
                          <div className="relative w-full h-full overflow-hidden shadow-lg">
                            <Image
                              src={item.image}
                              alt={item.title}
                              fill
                              className="object-cover"
                              sizes="(max-width: 1024px) 100vw, 50vw"
                              loading="lazy"
                            />
                          </div>
                        </div>
                      )}
                      <button
                        aria-label={`Learn more about ${item.title}`}
                        className="cursor-pointer absolute bottom-5 z-50 transition-opacity duration-300 group-hover:opacity-0 group-hover:hidden"
                      >
                        <Image
                          src={arrowIcon}
                          alt="Arrow"
                          width={48}
                          height={48}
                          className="size-10 md:size-12"
                        />
                      </button>
                    </>
                  );

                  return (
                    <div
                      key={index}
                      className={`
                        /* Base width for mobile: 100% of the parent container */
                        w-full min-w-full 
                     /* Responsive overrides */
                     md:min-w-[calc(50vw_-_50px)] 
                     lg:min-w-[calc(50vw_-_80px)] 
                     [@media(min-width:1365px)]:min-w-[calc(25%_-_22px)] 
                     [@media(min-width:1365px)]:w-[calc(25%_-_22px)]

                     ${
                       shouldAnimate
                         ? "[@media(min-width:1365px)]:h-[0px]"
                         : "h-auto [@media(min-width:2000px)]:h-[700px]"
                     } 
                     ${cardBg} relative industry-item 
                     ${
                       shouldAnimate
                         ? `item-${(index % industries.length) + 1} animation-item`
                         : ""
                     } group overflow-hidden`}
                    >
                      {!isTechnologyPage ? (
                        <div
                          className={`${itemContentClassName} flex flex-col justify-start !pt-10`}
                        >
                          {CardInner}
                        </div>
                      ) : (
                        <Link
                          href={item?.link || "/"}
                          className={`${itemContentClassName} flex flex-col justify-start !pt-10`}
                        >
                          {CardInner}
                        </Link>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="slider-navigation min-h-[var(--banner-title)] items-center justify-between flex mt-5 lg:mt-0">
              <div
                className={
                  shouldAnimate
                    ? "slider-progress-bar hidden [@media(min-width:1365px)]:block bg-white w-[20%] h-[12px] opacity-0"
                    : "hidden"
                }
              >
                <div
                  className="progress bg-[#225BD4] h-full transition-all duration-300"
                  style={{
                    width: `${((currentIndex % industries.length) / (industries.length - 1)) * 100}%`,
                  }}
                />
              </div>
              {titleEnd}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
